import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CartBottomSheet } from '@/components/cart/cart-bottom-sheet';
import { CartTrigger } from '@/components/cart/cart-trigger';
import { useCartStore } from '@/features/cart/stores/cart-store';
import { resolvePricingConfig } from '@/features/settings/pricing-config';
import { getSupportPhone, getWhatsAppNumber, useSettings } from '@/features/settings/hooks/use-settings';

export interface FloatingActionsProps {
  cartItemCount?: number;
  onCartClick?: () => void;
  phoneNumber?: string;
  whatsappNumber?: string;
  isLoggedIn?: boolean;
  userRole?: string;
  onAdminClick?: () => void;
  onRiderClick?: () => void;
  onLoginClick?: () => void;
  className?: string;
}

const IDLE_PULSE_DELAY_MS = 1800;
const ANIMATION_RESET_MS = 520;

export function FloatingActions({
  cartItemCount = 0,
  onCartClick,
  phoneNumber: providedPhoneNumber,
  whatsappNumber: providedWhatsappNumber,
  isLoggedIn: _isLoggedIn,
  userRole,
  onAdminClick,
  onRiderClick,
  onLoginClick,
  className,
}: FloatingActionsProps) {
  const navigate = useNavigate();
  const phoneNumber = providedPhoneNumber || getSupportPhone();
  const whatsappNumber = providedWhatsappNumber || getWhatsAppNumber();
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [isTriggerHighlighted, setIsTriggerHighlighted] = useState(false);
  const [shouldPulse, setShouldPulse] = useState(false);
  const pulseTimerRef = useRef<number | null>(null);
  const highlightTimerRef = useRef<number | null>(null);

  const {
    items,
    subtotal,
    deliveryFee,
    getTotal,
    itemCount: storeItemCount,
    isOpen,
    openCart,
    closeCart,
    lastAddedItem,
  } = useCartStore((state) => ({
    items: state.items,
    subtotal: state.subtotal,
    deliveryFee: state.deliveryFee,
    getTotal: state.getTotal,
    itemCount: state.itemCount,
    isOpen: state.isOpen,
    openCart: state.openCart,
    closeCart: state.closeCart,
    lastAddedItem: state.lastAddedItem,
  }));
  const { settings } = useSettings();
  const { freeDeliveryThreshold } = resolvePricingConfig(settings);

  const effectiveItemCount = Math.max(storeItemCount, cartItemCount);

  useEffect(() => {
    if (effectiveItemCount === 0 && isOpen) {
      closeCart();
    }
  }, [closeCart, effectiveItemCount, isOpen]);

  useEffect(() => {
    if (highlightTimerRef.current) {
      window.clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }

    if (!lastAddedItem || effectiveItemCount === 0) {
      setIsTriggerHighlighted(false);
      return;
    }

    setIsTriggerHighlighted(true);
    highlightTimerRef.current = window.setTimeout(() => {
      setIsTriggerHighlighted(false);
    }, ANIMATION_RESET_MS);

    return () => {
      if (highlightTimerRef.current) {
        window.clearTimeout(highlightTimerRef.current);
      }
    };
  }, [effectiveItemCount, lastAddedItem]);

  useEffect(() => {
    if (pulseTimerRef.current) {
      window.clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = null;
    }

    if (effectiveItemCount <= 0 || isOpen) {
      setShouldPulse(false);
      return;
    }

    setShouldPulse(false);
    pulseTimerRef.current = window.setTimeout(() => {
      setShouldPulse(true);
      window.setTimeout(() => setShouldPulse(false), 1800);
    }, IDLE_PULSE_DELAY_MS);

    return () => {
      if (pulseTimerRef.current) {
        window.clearTimeout(pulseTimerRef.current);
      }
    };
  }, [effectiveItemCount, isOpen, lastAddedItem?.addedAt]);

  const formatCartMessage = useMemo(() => {
    if (items.length === 0) {
      return '';
    }

    const itemsList = items
      .map((item) => `- ${item.product.name}${item.variant ? ` (${item.variant.volume})` : ''} x${item.quantity} @ KES ${item.unitPrice}`)
      .join('\n');

    const total = getTotal();
    const delivery = deliveryFee > 0 ? `KES ${deliveryFee.toLocaleString()}` : 'Free';

    return `Hello! I would like to order:\n${itemsList}\n\nSubtotal: KES ${subtotal.toLocaleString()}\nDelivery: ${delivery}${subtotal >= freeDeliveryThreshold ? ` (Free over KES ${freeDeliveryThreshold})` : ''}\nTotal: KES ${total.toLocaleString()}`;
  }, [deliveryFee, freeDeliveryThreshold, getTotal, items, subtotal]);

  const handleOpenSheet = () => {
    onCartClick?.();
    openCart();
  };

  const handleWhatsAppShare = () => {
    setShowShareDialog(false);
    closeCart();
    navigate('/checkout?action=whatsapp');
  };

  const handleSmsShare = () => {
    setShowShareDialog(false);
    closeCart();
    navigate('/checkout?action=sms');
  };

  const handleTextOrder = () => {
    if (effectiveItemCount === 0) {
      return;
    }

    setShowShareDialog(true);
  };

  const handleCallOrder = () => {
    if (effectiveItemCount === 0) {
      return;
    }

    closeCart();
    navigate('/checkout?action=call');
  };

  const handleSupportCall = () => {
    if (!phoneNumber) {
      return;
    }

    window.location.href = `tel:${phoneNumber}`;
  };

  const handleWhatsAppInquiry = () => {
    if (!whatsappNumber) {
      return;
    }

    const message = encodeURIComponent('Hi Karebe! I would like to inquire about your products.');
    window.open(`https://wa.me/${whatsappNumber.replace(/\D/g, '')}?text=${message}`, '_blank');
  };

  return (
    <>
      <CartTrigger
        itemCount={effectiveItemCount}
        onClick={handleOpenSheet}
        isHighlighted={isTriggerHighlighted}
        shouldPulse={shouldPulse}
        className={className}
      />

      <CartBottomSheet
        open={isOpen && effectiveItemCount > 0}
        items={items}
        itemCount={effectiveItemCount}
        subtotal={subtotal}
        total={getTotal()}
        deliveryFee={deliveryFee}
        phoneNumber={phoneNumber}
        userRole={userRole}
        accountActionLabel={!userRole && onLoginClick ? 'Login' : undefined}
        onClose={closeCart}
        onTextOrder={handleTextOrder}
        onCallOrder={handleCallOrder}
        onAccountAction={!userRole && onLoginClick ? onLoginClick : undefined}
        onAdminClick={onAdminClick}
        onRiderClick={onRiderClick}
        onSupportCall={handleSupportCall}
        onWhatsAppInquiry={handleWhatsAppInquiry}
      />

      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Your Order</DialogTitle>
            <DialogDescription>
              We will create the order first at checkout, then open WhatsApp or SMS with your saved order reference.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <div className="max-h-40 overflow-y-auto rounded-lg bg-gray-50 p-3">
              <p className="mb-2 text-xs text-gray-500">Message preview:</p>
              <p className="whitespace-pre-wrap text-sm text-gray-700">{formatCartMessage}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={handleWhatsAppShare}
                className="flex-1 gap-2 bg-green-500 text-white hover:bg-green-600"
              >
                <MessageCircle className="h-5 w-5" />
                WhatsApp
              </Button>

              <Button
                onClick={handleSmsShare}
                className="flex-1 gap-2 bg-blue-500 text-white hover:bg-blue-600"
              >
                <MessageSquare className="h-5 w-5" />
                SMS
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowShareDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default FloatingActions;
